import { type DBSchema, type IDBPDatabase, openDB } from "idb";

interface PanoramaCache {
  id: string;
  name: string;
  projectId: string;
  craftspaceId: string;
  data: ArrayBuffer;
  mimeType: string;
  size: number;
  lastAccessed: number;
  uploadedAt: number;
}

interface ThumbnailCache {
  id: string;
  panoramaId: string;
  projectId: string;
  craftspaceId: string;
  data: ArrayBuffer;
  mimeType: string;
  size: number;
  lastAccessed: number;
  uploadedAt: number;
}

interface CacheMetadata {
  id: string;
  type: "panorama" | "thumbnail";
  size: number;
  lastAccessed: number;
  uploadedAt: number;
}

interface PanoramaCacheDB extends DBSchema {
  panoramas: {
    key: string; // panoramaId
    value: PanoramaCache;
    indexes: {
      "by-project": string; // projectId
      "by-craftspace": string; // craftspaceId
      "by-last-accessed": number; // lastAccessed timestamp
    };
  };
  thumbnails: {
    key: string; // thumbnailId (panoramaId + '_thumb' or panoramaId + '_webp')
    value: ThumbnailCache;
    indexes: {
      "by-panorama": string; // panoramaId
      "by-project": string; // projectId
      "by-craftspace": string; // craftspaceId
      "by-last-accessed": number; // lastAccessed timestamp
    };
  };
  metadata: {
    key: string; // 'cache-stats'
    value: {
      totalSize: number;
      panoramaCount: number;
      thumbnailCount: number;
      lastCleanup: number;
    };
  };
}

class IndexedDBService {
  private db: IDBPDatabase<PanoramaCacheDB> | null = null;

  get isReady(): boolean {
    return this.db !== null;
  }
  private readonly DB_NAME = "360CrafterCache";
  private readonly DB_VERSION = 2;
  private readonly MAX_CACHE_SIZE = 500 * 1024 * 1024; // 500MB
  private readonly CLEANUP_THRESHOLD = 0.8; // Cleanup when 80% full

  async init(): Promise<void> {
    try {
      this.db = await openDB<PanoramaCacheDB>(this.DB_NAME, this.DB_VERSION, {
        upgrade: (db, oldVersion, _newVersion) => {
          // console.log('🔄 IndexedDB upgrade:', { oldVersion, newVersion });

          if (oldVersion < 1) {
            // Create stores for version 1
            const panoramaStore = db.createObjectStore("panoramas", { keyPath: "id" });
            panoramaStore.createIndex("by-project", "projectId");
            panoramaStore.createIndex("by-craftspace", "craftspaceId");
            panoramaStore.createIndex("by-last-accessed", "lastAccessed");

            const thumbnailStore = db.createObjectStore("thumbnails", { keyPath: "id" });
            thumbnailStore.createIndex("by-panorama", "panoramaId");
            thumbnailStore.createIndex("by-project", "projectId");
            thumbnailStore.createIndex("by-craftspace", "craftspaceId");
            thumbnailStore.createIndex("by-last-accessed", "lastAccessed");

            const _metadataStore = db.createObjectStore("metadata", { keyPath: "id" });
            // console.log('✅ Created object stores for version 1');
          }

          if (oldVersion < 2) {
            // Any future schema changes would go here
            // console.log('✅ Schema upgrade to version 2 complete');
          }
        },
      });

      // Initialize metadata if it doesn't exist
      try {
        const metadata = await this.db.get("metadata", "cache-stats");
        if (!metadata) {
          console.log("📊 Initializing cache metadata...");
          await this.db.put("metadata", {
            id: "cache-stats",
            totalSize: 0,
            panoramaCount: 0,
            thumbnailCount: 0,
            lastCleanup: Date.now(),
          });
          // console.log('✅ Cache metadata initialized');
        } else {
        }
      } catch (metadataError) {
        console.warn("⚠️ Failed to initialize metadata (continuing):", metadataError);
      }
    } catch (error) {
      console.error("❌ Failed to initialize IndexedDB:", error);
      throw error;
    }
  }

  private async ensureDB(): Promise<IDBPDatabase<PanoramaCacheDB>> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error("IndexedDB not initialized");
    }
    return this.db;
  }

  // Panorama caching methods
  async cachePanorama(
    panoramaId: string,
    projectId: string,
    craftspaceId: string,
    data: ArrayBuffer,
    mimeType: string,
    name: string,
  ): Promise<void> {
    try {
      const db = await this.ensureDB();
      const size = data.byteLength;

      // Check if we need to cleanup before adding
      await this.ensureCacheSpace(size);

      const panorama: PanoramaCache = {
        id: panoramaId,
        name,
        projectId,
        craftspaceId,
        data,
        mimeType,
        size,
        lastAccessed: Date.now(),
        uploadedAt: Date.now(),
      };

      await db.put("panoramas", panorama);
      await this.updateMetadata("panorama", size, 1);

      // console.log(`✅ Cached panorama ${name} (${size / 1024 / 1024).toFixed(2)}MB`);
    } catch (error) {
      console.error("❌ Failed to cache panorama:", error);
      throw error;
    }
  }

  async getPanorama(panoramaId: string): Promise<PanoramaCache | null> {
    try {
      const db = await this.ensureDB();
      const panorama = await db.get("panoramas", panoramaId);

      if (panorama) {
        // Update last accessed time
        panorama.lastAccessed = Date.now();
        await db.put("panoramas", panorama);
      }

      return panorama || null;
    } catch (error) {
      console.error("❌ Failed to get panorama from cache:", error);
      return null;
    }
  }

  async getPanoramasByProject(projectId: string): Promise<PanoramaCache[]> {
    try {
      const db = await this.ensureDB();
      const panoramas = await db.getAllFromIndex("panoramas", "by-project", projectId);

      // Update last accessed time for all
      for (const panorama of panoramas) {
        panorama.lastAccessed = Date.now();
        await db.put("panoramas", panorama);
      }

      return panoramas;
    } catch (error) {
      console.error("❌ Failed to get panoramas by project:", error);
      return [];
    }
  }

  // Thumbnail caching methods
  async cacheThumbnail(
    panoramaId: string,
    projectId: string,
    craftspaceId: string,
    data: ArrayBuffer,
    mimeType: string,
    isWebP: boolean = false,
  ): Promise<void> {
    try {
      const db = await this.ensureDB();
      const size = data.byteLength;
      const thumbnailId = isWebP ? `${panoramaId}_webp` : `${panoramaId}_thumb`;

      // Check if we need to cleanup before adding
      await this.ensureCacheSpace(size);

      const thumbnail: ThumbnailCache = {
        id: thumbnailId,
        panoramaId,
        projectId,
        craftspaceId,
        data,
        mimeType,
        size,
        lastAccessed: Date.now(),
        uploadedAt: Date.now(),
      };

      await db.put("thumbnails", thumbnail);
      await this.updateMetadata("thumbnail", size, 1);

      // console.log(`✅ Cached thumbnail ${thumbnailId} (${(size / 1024).toFixed(2)}KB)`);
    } catch (error) {
      console.error("❌ Failed to cache thumbnail:", error);
      throw error;
    }
  }

  async getThumbnail(panoramaId: string, isWebP: boolean = false): Promise<ThumbnailCache | null> {
    try {
      const db = await this.ensureDB();
      const thumbnailId = isWebP ? `${panoramaId}_webp` : `${panoramaId}_thumb`;
      const thumbnail = await db.get("thumbnails", thumbnailId);

      if (thumbnail) {
        // Update last accessed time
        thumbnail.lastAccessed = Date.now();
        await db.put("thumbnails", thumbnail);
      }

      return thumbnail || null;
    } catch (error) {
      console.error("❌ Failed to get thumbnail from cache:", error);
      return null;
    }
  }

  async getThumbnailsByProject(projectId: string): Promise<ThumbnailCache[]> {
    try {
      const db = await this.ensureDB();
      const thumbnails = await db.getAllFromIndex("thumbnails", "by-project", projectId);

      // Update last accessed time for all
      for (const thumbnail of thumbnails) {
        thumbnail.lastAccessed = Date.now();
        await db.put("thumbnails", thumbnail);
      }

      return thumbnails;
    } catch (error) {
      console.error("❌ Failed to get thumbnails by project:", error);
      return [];
    }
  }

  async clearPanoramaCache(panoramaId: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      const panorama = await db.get("panoramas", panoramaId);
      if (panorama) {
        await db.delete("panoramas", panoramaId);
        await this.updateMetadata("panorama", -panorama.size, -1);
      }

      const thumbId = `${panoramaId}_thumb`;
      const webpId = `${panoramaId}_webp`;
      const thumb = await db.get("thumbnails", thumbId);
      if (thumb) {
        await db.delete("thumbnails", thumbId);
        await this.updateMetadata("thumbnail", -thumb.size, -1);
      }
      const webp = await db.get("thumbnails", webpId);
      if (webp) {
        await db.delete("thumbnails", webpId);
        await this.updateMetadata("thumbnail", -webp.size, -1);
      }
    } catch (error) {
      console.error("❌ Failed to clear panorama cache:", error);
    }
  }

  // Cache thumbnail from URL (for re-caching when cache is cleared)
  async cacheThumbnailFromUrl(
    panoramaId: string,
    projectId: string,
    craftspaceId: string,
    url: string,
    isWebP: boolean = false,
  ): Promise<void> {
    // app:// URLs are served natively by Electron and cannot be fetched via the Fetch API
    if (url.startsWith("app://")) return;

    try {
      // console.log(`🔄 Fetching thumbnail from URL for re-caching: ${panoramaId}`);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      await this.cacheThumbnail(
        panoramaId,
        projectId,
        craftspaceId,
        arrayBuffer,
        isWebP ? "image/webp" : "image/jpeg",
        isWebP,
      );

      // console.log(`✅ Successfully re-cached thumbnail from URL: ${panoramaId}`);
    } catch (error) {
      console.error("❌ Failed to re-cache thumbnail from URL:", error);
      throw error;
    }
  }

  // Cache management methods
  private async ensureCacheSpace(requiredSize: number): Promise<void> {
    try {
      const db = await this.ensureDB();
      const metadata = await db.get("metadata", "cache-stats");

      if (!metadata) return;

      const projectedSize = metadata.totalSize + requiredSize;

      if (projectedSize > this.MAX_CACHE_SIZE * this.CLEANUP_THRESHOLD) {
        await this.cleanupCache(projectedSize - this.MAX_CACHE_SIZE * 0.5); // Clean to 50% capacity
      }
    } catch (error) {
      console.error("❌ Failed to ensure cache space:", error);
    }
  }

  private async cleanupCache(targetReduction: number): Promise<void> {
    try {
      const db = await this.ensureDB();
      let reducedSize = 0;

      // Get all items sorted by last accessed (oldest first)
      const panoramas = await db.getAllFromIndex("panoramas", "by-last-accessed");
      const thumbnails = await db.getAllFromIndex("thumbnails", "by-last-accessed");

      const allItems = [
        ...panoramas.map((p) => ({ ...p, type: "panorama" as const })),
        ...thumbnails.map((t) => ({ ...t, type: "thumbnail" as const })),
      ].sort((a, b) => a.lastAccessed - b.lastAccessed);

      // Remove oldest items until we reach target reduction
      for (const item of allItems) {
        if (reducedSize >= targetReduction) break;

        if (item.type === "panorama") {
          await db.delete("panoramas", item.id);
          reducedSize += item.size;
        } else {
          await db.delete("thumbnails", item.id);
          reducedSize += item.size;
        }
      }

      // Update metadata
      await this.updateMetadata("cleanup", -reducedSize, 0);

      console.log(`🧹 Cache cleanup: freed ${(reducedSize / 1024 / 1024).toFixed(2)}MB`);
    } catch (error) {
      console.error("❌ Failed to cleanup cache:", error);
    }
  }

  private async updateMetadata(
    type: "panorama" | "thumbnail" | "cleanup",
    sizeChange: number,
    countChange: number,
  ): Promise<void> {
    try {
      const db = await this.ensureDB();
      const metadata = await db.get("metadata", "cache-stats");

      if (metadata) {
        metadata.totalSize = Math.max(0, metadata.totalSize + sizeChange);
        if (type === "panorama")
          metadata.panoramaCount = Math.max(0, metadata.panoramaCount + countChange);
        if (type === "thumbnail")
          metadata.thumbnailCount = Math.max(0, metadata.thumbnailCount + countChange);
        if (type === "cleanup") metadata.lastCleanup = Date.now();

        await db.put("metadata", metadata);
      }
    } catch (error) {
      console.error("❌ Failed to update metadata:", error);
    }
  }

  // Utility methods
  async getCacheStats(): Promise<{
    totalSize: number;
    panoramaCount: number;
    thumbnailCount: number;
    lastCleanup: number;
  } | null> {
    try {
      const db = await this.ensureDB();
      return (await db.get("metadata", "cache-stats")) || null;
    } catch (error) {
      console.error("❌ Failed to get cache stats:", error);
      return null;
    }
  }

  async clearProjectCache(projectId: string): Promise<void> {
    try {
      const db = await this.ensureDB();

      // Get all panoramas and thumbnails for the project
      const panoramas = await db.getAllFromIndex("panoramas", "by-project", projectId);
      const thumbnails = await db.getAllFromIndex("thumbnails", "by-project", projectId);

      let totalSize = 0;

      // Delete panoramas
      for (const panorama of panoramas) {
        await db.delete("panoramas", panorama.id);
        totalSize += panorama.size;
      }

      // Delete thumbnails
      for (const thumbnail of thumbnails) {
        await db.delete("thumbnails", thumbnail.id);
        totalSize += thumbnail.size;
      }

      // Update metadata
      await this.updateMetadata("cleanup", -totalSize, 0);

      console.log(`🗑️ Cleared project cache: freed ${(totalSize / 1024 / 1024).toFixed(2)}MB`);
    } catch (error) {
      console.error("❌ Failed to clear project cache:", error);
    }
  }

  async clearAllCache(): Promise<void> {
    try {
      const db = await this.ensureDB();
      await db.clear("panoramas");
      await db.clear("thumbnails");

      // Reset metadata
      await db.put("metadata", {
        id: "cache-stats",
        totalSize: 0,
        panoramaCount: 0,
        thumbnailCount: 0,
        lastCleanup: Date.now(),
      });

      console.log("🗑️ Cleared all cache");
    } catch (error) {
      console.error("❌ Failed to clear all cache:", error);
    }
  }

  // Convert ArrayBuffer to Blob URL for display
  createBlobURL(data: ArrayBuffer, mimeType: string): string {
    const blob = new Blob([data], { type: mimeType });
    return URL.createObjectURL(blob);
  }

  // Revoke Blob URL to free memory
  revokeBlobURL(url: string): void {
    URL.revokeObjectURL(url);
  }
}

// Export singleton instance
export const indexedDBService = new IndexedDBService();

// Export types for use in other files
export type { PanoramaCache, ThumbnailCache, CacheMetadata };
